/**
 * RateAlertsTab — self-serve Rate Alert email management on the Mortgage
 * Monitor page. Each user controls whether they get the alert email, how
 * often, and exactly which loans qualify (per-loan-type criteria, equity
 * alerts, only-new). Emails send via SendGrid with Open-in-Origin and
 * Open-in-Arive buttons per loan.
 *
 * Admin view-as: when the page's user selector points at another LO,
 * viewAsUid/viewAsName manage THAT user's alerts; test emails deliver to the
 * admin's own inbox.
 */

import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Skeleton,
  Switch,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FiBell, FiMail, FiSave, FiSend } from 'react-icons/fi';
import { useRateAlerts, RATE_ALERT_LOAN_TYPES } from '../../../hooks/useRateAlerts';

const TYPE_LABELS = {
  Conventional: 'Conventional',
  FHA: 'FHA',
  VA: 'VA',
  USDA: 'USDA',
  NonQM: 'Non-QM',
  Other: 'Other / Unknown',
};

const WEEKDAYS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
];

const fmtLastSent = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'never';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export default function RateAlertsTab({ viewAsUid = null, viewAsName = null }) {
  const toast = useToast();
  const {
    settings, lastSentAt, loading, saving, sendingTest, dirty, error,
    update, updateType, save, sendTest,
  } = useRateAlerts(viewAsUid);

  const impersonating = !!viewAsUid;
  const viewLabel = viewAsName || 'this user';

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const typeCardBg = useColorModeValue('gray.50', 'gray.900');

  const numberField = (setter) => (str, num) => {
    setter(str === '' || !Number.isFinite(num) ? null : num);
  };

  const handleSave = async () => {
    const result = await save();
    if (result.success) {
      toast({
        title: impersonating ? `Rate alert settings saved for ${viewLabel}` : 'Rate alert settings saved',
        status: 'success', duration: 4000, isClosable: true,
      });
    } else {
      toast({ title: 'Save failed', description: result.error, status: 'error', duration: 6000, isClosable: true });
    }
  };

  const handleSendTest = async () => {
    const result = await sendTest();
    if (result.success) {
      toast({
        title: result.sent
          ? (result.deliveredToCaller ? `Test sent to YOUR inbox (previewing ${viewLabel})` : 'Test alert sent — check your inbox')
          : 'No email sent',
        description: result.sent
          ? `${result.matched ?? 0} opportunit${(result.matched ?? 0) === 1 ? 'y' : 'ies'} matched ${impersonating ? `${viewLabel}'s` : 'your'} criteria.`
          : (result.message || `Nothing currently matches ${impersonating ? `${viewLabel}'s` : 'your'} criteria, so there was nothing to send.`),
        status: result.sent ? 'success' : 'info',
        duration: 7000,
        isClosable: true,
      });
    } else {
      toast({ title: 'Test send failed', description: result.error, status: 'error', duration: 6000, isClosable: true });
    }
  };

  if (loading) {
    return (
      <VStack align="stretch" spacing={4}>
        <Skeleton h="120px" borderRadius="lg" />
        <Skeleton h="320px" borderRadius="lg" />
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={5} maxW="1100px">
      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {impersonating && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Managing <strong>{viewLabel}</strong>&apos;s rate alerts. Scheduled alerts go to their inbox;
            &quot;Send me a test&quot; delivers their digest to <strong>your</strong> inbox.
          </Text>
        </Alert>
      )}

      {/* Master controls */}
      <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={5} data-tour="alerts-master">
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4}>
          <Box>
            <HStack spacing={2}>
              <Icon as={FiBell} color="blue.400" />
              <Heading size="sm">Rate Alert emails{impersonating ? ` — ${viewLabel}` : ''}</Heading>
              <Badge colorScheme={settings.enabled ? 'green' : 'gray'}>
                {settings.enabled ? 'ON' : 'OFF'}
              </Badge>
            </HStack>
            <Text fontSize="sm" color={subtleText} mt={1} maxW="560px">
              A personal email digest of {impersonating ? `${viewLabel}'s` : 'your'} Mortgage Monitor opportunities that match the criteria
              below — each loan links straight into Origin and Arive. Last sent: {fmtLastSent(lastSentAt)}.
            </Text>
          </Box>
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel htmlFor="alerts-enabled" mb={0} fontSize="sm">
              {impersonating ? 'Alerts enabled' : 'Send me alerts'}
            </FormLabel>
            <Switch
              id="alerts-enabled"
              colorScheme="green"
              isChecked={settings.enabled}
              onChange={(e) => update({ enabled: e.target.checked })}
            />
          </FormControl>
        </Flex>

        <Divider my={4} />

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
          <FormControl>
            <FormLabel fontSize="sm">Frequency</FormLabel>
            <Select
              size="sm"
              value={settings.frequency}
              onChange={(e) => update({ frequency: e.target.value })}
            >
              <option value="daily">Every weekday</option>
              <option value="weekly">Once a week</option>
            </Select>
            <FormHelperText fontSize="xs">Sends ~7:30am ET after the morning rate refresh + scan</FormHelperText>
          </FormControl>

          {settings.frequency === 'weekly' && (
            <FormControl>
              <FormLabel fontSize="sm">Day of week</FormLabel>
              <Select
                size="sm"
                value={settings.sendDay}
                onChange={(e) => update({ sendDay: Number(e.target.value) })}
              >
                {WEEKDAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </Select>
            </FormControl>
          )}

          <FormControl display="flex" flexDirection="column">
            <FormLabel fontSize="sm">Only new opportunities</FormLabel>
            <HStack>
              <Switch
                isChecked={settings.onlyNewSinceLast}
                onChange={(e) => update({ onlyNewSinceLast: e.target.checked })}
              />
              <Text fontSize="xs" color={subtleText}>
                Skip loans already included in a previous alert
              </Text>
            </HStack>
          </FormControl>
        </SimpleGrid>

        <Divider my={4} />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} maxW="700px">
          <FormControl display="flex" flexDirection="column">
            <FormLabel fontSize="sm">Include equity alerts</FormLabel>
            <HStack>
              <Switch
                isChecked={settings.includeEquityAlerts}
                onChange={(e) => update({ includeEquityAlerts: e.target.checked })}
              />
              <Text fontSize="xs" color={subtleText}>
                Flag equity-rich clients (cash-out / debt-consolidation angles)
              </Text>
            </HStack>
          </FormControl>
          {settings.includeEquityAlerts && (
            <FormControl>
              <FormLabel fontSize="sm">Min equity % for the equity flag</FormLabel>
              <NumberInput
                size="sm"
                min={0}
                max={100}
                value={settings.minEquityPct ?? ''}
                onChange={numberField((v) => update({ minEquityPct: v }))}
              >
                <NumberInputField placeholder="Monitor default" />
              </NumberInput>
              <FormHelperText fontSize="xs">Leave blank to use the monitor&apos;s global equity flag</FormHelperText>
            </FormControl>
          )}
        </SimpleGrid>
      </Box>

      {/* Per-loan-type criteria */}
      <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={5} data-tour="alerts-criteria">
        <Heading size="sm" mb={1}>Criteria by loan type</Heading>
        <Text fontSize="sm" color={subtleText} mb={4}>
          A loan makes {impersonating ? `${viewLabel}'s` : 'your'} alert when its type is enabled and it clears every filled-in minimum.
          Blank fields fall back to the monitor&apos;s global thresholds.
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          {RATE_ALERT_LOAN_TYPES.map((type) => {
            const criteria = settings.loanTypes[type] || {};
            return (
              <Box
                key={type}
                bg={typeCardBg}
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="md"
                p={4}
                opacity={criteria.enabled ? 1 : 0.55}
              >
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontWeight="700" fontSize="sm">{TYPE_LABELS[type]}</Text>
                  <Checkbox
                    isChecked={!!criteria.enabled}
                    onChange={(e) => updateType(type, { enabled: e.target.checked })}
                    size="sm"
                  >
                    <Text fontSize="xs">Include</Text>
                  </Checkbox>
                </Flex>
                <VStack align="stretch" spacing={2.5}>
                  <FormControl isDisabled={!criteria.enabled}>
                    <FormLabel fontSize="xs" mb={0.5}>Min rate delta (bps)</FormLabel>
                    <NumberInput size="xs" min={0} max={500} value={criteria.minRateDeltaBps ?? ''}
                      onChange={numberField((v) => updateType(type, { minRateDeltaBps: v }))}>
                      <NumberInputField placeholder="Monitor default" />
                    </NumberInput>
                  </FormControl>
                  <FormControl isDisabled={!criteria.enabled}>
                    <FormLabel fontSize="xs" mb={0.5}>Min savings ($/mo)</FormLabel>
                    <NumberInput size="xs" min={0} value={criteria.minMonthlySavings ?? ''}
                      onChange={numberField((v) => updateType(type, { minMonthlySavings: v }))}>
                      <NumberInputField placeholder="Monitor default" />
                    </NumberInput>
                  </FormControl>
                  <HStack spacing={2}>
                    <FormControl isDisabled={!criteria.enabled}>
                      <FormLabel fontSize="xs" mb={0.5}>Min balance</FormLabel>
                      <NumberInput size="xs" min={0} step={25000} value={criteria.minBalance ?? ''}
                        onChange={numberField((v) => updateType(type, { minBalance: v }))}>
                        <NumberInputField placeholder="Any" />
                      </NumberInput>
                    </FormControl>
                    <FormControl isDisabled={!criteria.enabled}>
                      <FormLabel fontSize="xs" mb={0.5}>Max balance</FormLabel>
                      <NumberInput size="xs" min={0} step={25000} value={criteria.maxBalance ?? ''}
                        onChange={numberField((v) => updateType(type, { maxBalance: v }))}>
                        <NumberInputField placeholder="Any" />
                      </NumberInput>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>
      </Box>

      {/* Actions */}
      <HStack spacing={3} data-tour="alerts-save">
        <Button
          colorScheme="blue"
          leftIcon={<FiSave />}
          onClick={handleSave}
          isLoading={saving}
          isDisabled={!dirty}
        >
          Save alert settings
        </Button>
        <Button
          variant="outline"
          leftIcon={sendingTest ? <FiMail /> : <FiSend />}
          onClick={handleSendTest}
          isLoading={sendingTest}
        >
          {impersonating ? 'Send me a test (their digest)' : 'Send me a test now'}
        </Button>
        {dirty && (
          <Text fontSize="xs" color="orange.400">Unsaved changes — the test uses the last saved settings.</Text>
        )}
      </HStack>
    </VStack>
  );
}
